import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subscription, Observable, forkJoin } from "rxjs";
import { tap, mergeMap, map } from "rxjs/operators";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { OnBaseIntegrationAndMappingService } from "../../../../common/services/onbase-integration-mapping.service";
import { OnBaseDocumentType, OnBaseDocumentGroup } from '../../../../common/models/onBaseIntegrationAndMapping.model';
import { RequestForScanning, ScanningSteps } from "../../../../common/models/scan-documents.model";
import { GroupService, GroupSearchSpec } from "../../../services/group.service";
import { Group } from "../../../models/group.model";

import { OrganizationService } from "../../../../common/services/organization.service";
import { Department, Organization } from "../../../../common/models/organization.model";

import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { LunaClaimTypes, LunaClaimValues } from "../../../../models/user-identity.model";
import { AuthService } from "../../../../services/auth.service";
import { WorkOrder, WorkOrderWithAssignments, WorkOrderType, WorkOrderStatus } from "../../../../common/models/work-order.model";
import { WorkOrderService } from "../../../../common/services/work-order.service";
import { DocumentManagementService, IUploadFile } from "../../../../common/services/document-management.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { ScanRequestService } from "../../../services/scan-request.service";
import { FlowIndex } from "../../../../common/models/flow.dto";



@Component({
    selector: 'app-confirm-scan',
    templateUrl: './confirm-scan.component.html',
    styleUrls: ['./confirm-scan.component.scss'],
    standalone: false
})
export class ConfirmScanComponent implements OnInit, OnDestroy {
    alreadyCompleteDialog: Dialog = new Dialog("This work order has already been completed.", [new DialogButton("Exit", false, () => { this.requestCompleted(); })]);

    commitConfirmation: Dialog = new Dialog("You are going to commit all documents associated with #[RequestID].  These documents will be uploaded into document management and tagged with the appropriate keywords.  Are you certain the documents are ready to be stored?", [new DialogButton("Cancel", false, () => { return; }), new DialogButton("Confirm", true, () => { this.saveDocuments(); })]);
    currentRequest: RequestForScanning;
    currentStep: ScanningSteps = ScanningSteps.Loading;
    departments: Department[];
    documentGroups: OnBaseDocumentGroup[];
    documentTypes: OnBaseDocumentType[];
    @ViewChild("downloadLink", { static: true }) download: ElementRef;
    enableButtons: boolean = false;

    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);

    get files(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.currentRequest) {
            if (this.currentRequest.files == null) {
                this.currentRequest.files = [];
            }
            files = this.currentRequest.files;
        }
        return files;
    }
    formGroup: FormGroup;
    get groupRequesting(): string {
        let group: string = "";
        if (this.currentRequest != null && this.departments != null) {
            const selected = this.departments.find(g => g.id === this.currentRequest.groupRequesting);
            if (selected != null) {
                group = selected.displayName;
            }
        }

        return group;
    }

    
    groups: Group[];
    index: FlowIndex;
    isRejecting: boolean = false;

    notImplementedDialog: Dialog = new Dialog("This function has not yet been implemented.", [new DialogButton("Sorry!", true, () => { return; })]);


    get originalRequest(): RequestForScanning {
        if (this.originalWorkOrder == null) return null;
        return this.originalWorkOrder.scanRequest;
    }
    _originalWorkOrder: WorkOrderWithAssignments;


    get originalWorkOrder(): WorkOrderWithAssignments {
        return this._originalWorkOrder;
    }

    set originalWorkOrder(value: WorkOrderWithAssignments) {
        if (value.status === WorkOrderStatus.Pending) { //this really shouldn't happen, but is
            value.status = WorkOrderStatus.Completed;
            this.subscription.add(this._workOrderService.changeStatus(value.id, WorkOrderStatus.Completed).subscribe(() => { }, (error) => { console.error(error); }));
        }
        this._originalWorkOrder = value;
    }
    

    rejectDialog: Dialog = new Dialog("Request #[RequestID] has been rejected.", [new DialogButton("OK", true, () => { this.requestCompleted(); })]);


    get requestId(): string {
        let id: string = "";
        if (this.requestWorkOrder) {
            id = this.requestWorkOrder.applicationNumber;
        }
        return id;
    }

    get requestor(): string {
        let group: string = "";
        if (this.currentRequest != null && this.groups != null) {
            const selected = this.groups.find(g => g.id === this.currentRequest.requestor);
            if (selected != null) {
                group = selected.displayName;
            }
        }
        return group;
    }

    requestWorkOrder: WorkOrder;

    saveSuccessDialog: Dialog = new Dialog("Documents for request #[RequestID] have been successfully uploaded.", [new DialogButton("Exit", true, () => { this.requestCompleted(); })]);

    steps = ScanningSteps;

    subscription: Subscription = new Subscription();

    constructor(private builder: FormBuilder, public route: ActivatedRoute, public router: Router, public dialogService: DynamicDialogService, public subheader: SubHeaderService, private _workOrderService: WorkOrderService, public loader: LoaderService,
        private _groupService: GroupService, private _organizationService: OrganizationService, public auth: AuthService, private _documentService: DocumentManagementService, private _requestService: ScanRequestService) { }

    ngOnInit() {
        this.buildForm();

        let organization$: Observable<Organization> = this._organizationService.getOrganization(this.auth.User.organizationId).pipe(tap((org: Organization) => {
            this.departments = org.departments.sort((a, b) => {
                if (a.displayName > b.displayName) return 1;
                if (a.displayName < b.displayName) return -1;
                return 0;
            });
        }));

        let group$: Observable<Group[]> = this._groupService.get({ internal: true, stubs: false } as GroupSearchSpec).pipe(map((groupData) => {
            let scanEligible: Group[] = (groupData as Group[]).filter(g => g.permissions.find(c => c.type === LunaClaimTypes.Widget && c.value === LunaClaimValues.ViewScanningWidget) != null);
            this.groups = scanEligible;
            return this.groups;
        }));

        const routeSubs$ = this.route.data.pipe(
            tap((data) => {
                this.loader.toggleLoader(false);
                if (data.workOrder) {
                    this.requestWorkOrder = data.workOrder;
                    this.currentRequest = this.requestWorkOrder.scanRequest;
                }
                this.subheader.updateSubHeader({ title: `Document Scanning Request ${this.requestId}`, buttons: [{ name: "Download Original Cover Sheet", method: () => { this.downloadCoversheet(); } }] });               
            }),
            mergeMap(() => {
                return forkJoin(organization$, group$);
            }),
            mergeMap(() => {
                return this._workOrderService.getApplicationWorkOrders(this.requestWorkOrder.applicationNumber, [WorkOrderType.ScanDocuments], []);
            }),
            tap((previousOrders: WorkOrderWithAssignments[]) => {
                let sorted = previousOrders.sort((a, b) => {
                    if (a.lastUpdatedDate < b.lastUpdatedDate) return -1;
                    if (b.lastUpdatedDate < a.lastUpdatedDate) return 1;
                    return 0;
                });
                this.originalWorkOrder = sorted[0];
            }),
            mergeMap(() => {
                return this._requestService.getScanRequest(this.requestId);
            })
        ).subscribe(
            (flowIndex: FlowIndex) => {
                this.index = flowIndex;
                if (this.requestWorkOrder.status === WorkOrderStatus.Completed || this.requestWorkOrder.status === WorkOrderStatus.Cancelled) {
                    this.workOrderCompleted();
                    return;
                }
                if (this.index.state !== ScanningSteps.DocumentGroup) {
                    this.router.navigate(['/admin/scanning/request', this.requestWorkOrder.id]);
                }
                this.currentStep = ScanningSteps.DocumentGroup;
                this.initializeForm();
                this.enableButtons = true;
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(routeSubs$);
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }


    buildForm(): void {
        this.formGroup = this.builder.group({
            receivedDate: [{ value: "", disabled: true }, []],          
            confidential: [{ value: false, disabled: true }, []],
            legalReview: [{ value: false, disabled: true }, []],
            rejectReason: ["", [Validators.required]]
        });        
    }

    downloadCoversheet(): void {
        this.loader.toggleLoader(true);
        let subs$ = this._documentService.downloadFile(this.originalRequest.coversheetUrl).pipe()
            .subscribe(
                (data: string) => {
                    const link = this.download.nativeElement;
                    link.href = data;
                    link.click();
                    this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);                   
                    this.handleError(error);
                }
            );
        this.subscription.add(subs$);
    }


    handleError(error: any): void {
        this.loader.toggleLoader(false);
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }


    initializeForm(): void {
        if (this.currentRequest) {
            this.formGroup.get("receivedDate").setValue(this.currentRequest.dateDocumentReceived);
            this.formGroup.get("confidential").setValue(this.currentRequest.confidential);
            this.formGroup.get("legalReview").setValue(this.currentRequest.legalReviewRequired);
        }
    }

    notImplemented(): void {
        this.notImplementedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.notImplementedDialog);
    }

    documentGroupConfirm(): void {
        this.commitConfirmation.message = this.commitConfirmation.message.replace("[RequestID]", this.requestId);
        this.commitConfirmation.toggleDialog(true);
        this.dialogService.updateDialog(this.commitConfirmation);
    }


    rejectScan(): void {
        this.loader.toggleLoader(true);
        this.currentRequest.rejectReason = this.formGroup.get("rejectReason").value;
        let save$ = this._requestService.updateScanRequest("Reject", this.requestWorkOrder.id, this.currentRequest).pipe()
            .subscribe(
                (order: WorkOrder) => {
                    this.loader.toggleLoader(false);
                    this.rejectDialog.message = this.rejectDialog.message.replace("[RequestID]", order.applicationNumber);
                    this.rejectDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.rejectDialog);
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                    this.handleError(error);
                }
        );
        this.subscription.add(save$);
    }


    requestCompleted(): void {
        this.router.navigate(["/home"]);
    }

    saveDocuments(): void {
        this.loader.toggleLoader(true);
        const saveObs$ = this._documentService.commitScanRequest(this.requestWorkOrder.id, this.currentRequest).pipe(tap((docIds: number[]) => {
            this.loader.toggleLoader(false);
        })).subscribe(
            (docIds: number[]) => {
                const docString = docIds.join(", ");
                this.saveSuccessDialog.message = this.saveSuccessDialog.message.replace("[RequestID]", this.requestId);
                this.saveSuccessDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.saveSuccessDialog);
            },
            (error) => {
                console.error(error);
                this.handleError(error);
            }
        );
        this.subscription.add(saveObs$);
    }

    toggleReject(): void {
        this.isRejecting = !this.isRejecting;
        let ctrl = this.formGroup.get("rejectReason");
        if (ctrl) {
            ctrl.setValue(this.currentRequest.rejectReason);
            ctrl.updateValueAndValidity();
        }
    }

    workOrderCompleted(): void {
        this.alreadyCompleteDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.alreadyCompleteDialog);
    }
}

